import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { STUDENT_STATUSES, STUDENT_STATUS_LABEL } from '@doit/shared';
import { Download, GraduationCap, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { ConfirmDialog } from '../../components/Modal';
import { Button, EmptyState, PageHeader, Spinner } from '../../components/ui';
import { Select } from '../../components/form';
import { Badge, ProjectBadge } from '../../components/badges';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../api/client';
import {
  downloadCsv,
  useDeleteStudent,
  useFlows,
  useGroups,
  useProjects,
  useStudents,
} from '../../api/queries';
import { CertBadge, DoneCell } from '../../components/certificate';
import type { StudentRow } from '../../api/types';
import { useDebounced } from '../../lib/useDebounced';
import { StudentModal } from './StudentModal';

const PAGE_SIZE = 40;

export function StudentsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [projectId, setProjectId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [flowId, setFlowId] = useState('');
  const [status, setStatus] = useState('');
  const [unassigned, setUnassigned] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<StudentRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [toDelete, setToDelete] = useState<StudentRow | null>(null);
  const [exporting, setExporting] = useState(false);

  const debouncedQ = useDebounced(q, 300);
  const { data: projects } = useProjects();
  const { data: flows } = useFlows(projectId || undefined);
  // ჯგუფების სია არჩეულ პროექტსა და ნაკადს მიჰყვება
  const { data: groupList } = useGroups({
    projectId: projectId || undefined,
    flowId: flowId || undefined,
    pageSize: 200,
  });
  const remove = useDeleteStudent();

  const { data, isLoading } = useStudents({
    q: debouncedQ || undefined,
    projectId: projectId || undefined,
    groupId: groupId || undefined,
    flowId: flowId || undefined,
    status: status || undefined,
    unassigned: unassigned || undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  const students = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  function resetPage<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(1);
    };
  }

  /** ექსპორტი — ზუსტად ის, რაც ფილტრებით ჩანს (გვერდების გარეშე) */
  async function handleExport() {
    setExporting(true);
    try {
      await downloadCsv('students-filtered', {
        q: debouncedQ || undefined,
        projectId: projectId || undefined,
        groupId: groupId || undefined,
        flowId: flowId || undefined,
        status: status || undefined,
        unassigned: unassigned || undefined,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'ექსპორტი ვერ მოხერხდა');
    } finally {
      setExporting(false);
    }
  }

  async function handleDelete() {
    if (!toDelete) return;
    try {
      await remove.mutateAsync(toDelete.id);
      toast.success('სტუდენტი წაიშალა');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'წაშლა ვერ მოხერხდა');
    } finally {
      setToDelete(null);
    }
  }

  return (
    <>
      <PageHeader
        title="სტუდენტები"
        subtitle={isLoading ? 'იტვირთება…' : `${data?.total ?? 0} ჩანაწერი`}
        action={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              icon={<Download className="size-4" />}
              onClick={() => void handleExport()}
              loading={exporting}
              disabled={!data?.total}
              title="ჩამოტვირთვა Excel-ისთვის (CSV) — მიმდინარე ფილტრებით"
            >
              ექსპორტი
            </Button>
            <Button
              icon={<Plus className="size-4" />}
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              ახალი სტუდენტი
            </Button>
          </div>
        }
      />

      {/* --- ფილტრები --- */}
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="relative lg:col-span-2">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-mute" />
          <input
            value={q}
            onChange={(e) => resetPage(setQ)(e.target.value)}
            placeholder="სახელი, ელფოსტა ან ტელეფონი…"
            className="w-full rounded-lg border border-line-strong bg-surface py-2.5 pl-9 pr-3 text-sm transition placeholder:text-ink-mute focus:border-brand-600 focus:outline-none focus:ring-3 focus:ring-brand-100"
          />
        </div>
        <Select
          value={projectId}
          onChange={(e) => {
            resetPage(setProjectId)(e.target.value);
            setFlowId('');
            setGroupId('');
          }}
          placeholder="ყველა პროექტი"
          options={(projects ?? []).map((p) => ({ value: p.id, label: p.name }))}
        />
        <Select
          value={flowId}
          onChange={(e) => {
            resetPage(setFlowId)(e.target.value);
            setGroupId('');
          }}
          placeholder="ყველა ნაკადი"
          options={(flows ?? []).map((f) => ({ value: f.id, label: f.name }))}
        />
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Select
          value={groupId}
          onChange={(e) => resetPage(setGroupId)(e.target.value)}
          placeholder="ყველა ჯგუფი"
          options={(groupList?.items ?? []).map((g) => ({ value: g.id, label: g.name }))}
        />
        <Select
          value={status}
          onChange={(e) => resetPage(setStatus)(e.target.value)}
          placeholder="ყველა სტატუსი"
          options={STUDENT_STATUSES.map((s) => ({ value: s, label: STUDENT_STATUS_LABEL[s] }))}
        />
        <Select
          value={unassigned}
          onChange={(e) => resetPage(setUnassigned)(e.target.value)}
          placeholder="ჯგუფის მიხედვით — ყველა"
          options={[{ value: 'true', label: 'მხოლოდ ჯგუფის გარეშე' }]}
        />
      </div>

      {/* --- ცხრილი --- */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-6" />
        </div>
      ) : students.length === 0 ? (
        <div className="rounded-[10px] border border-line bg-surface shadow-xs">
          <EmptyState
            icon={<GraduationCap className="size-8" />}
            title="სტუდენტები ვერ მოიძებნა"
            description="შეცვალე ფილტრები ან დაამატე ახალი სტუდენტი."
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-[10px] border border-line bg-surface shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-canvas/70 text-left text-[11px] uppercase tracking-wide text-ink-mute">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">სტუდენტი</th>
                  <th className="px-4 py-2.5 font-semibold">კონტაქტი</th>
                  <th className="px-4 py-2.5 font-semibold">ჯგუფ(ებ)ი</th>
                  <th className="px-4 py-2.5 font-semibold">პროექტი</th>
                  <th className="px-4 py-2.5 font-semibold">შესრულებული</th>
                  <th className="px-4 py-2.5 font-semibold">სერტიფიკატი</th>
                  <th className="px-4 py-2.5 font-semibold">სტატუსი</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {students.map((s) => {
                  const active = s.enrollments.filter((e) => e.status === 'ACTIVE');
                  // შეფასება არჩეული ჯგუფისა, თორემ პირველი აქტიური ჩარიცხვის
                  const shown =
                    (groupId && s.enrollments.find((e) => e.group.id === groupId)) ||
                    active[0] ||
                    s.enrollments[0];
                  return (
                    <tr
                      key={s.id}
                      onClick={() => navigate(`/students/${s.id}`)}
                      className="cursor-pointer border-t border-line transition hover:bg-canvas/60"
                      title="ქარდის გახსნა"
                    >
                      <td className="px-4 py-3">
                        <span className="font-medium text-ink hover:text-brand-700">
                          {s.fullName}
                        </span>
                        {s.academyEmail && (
                          <span className="block text-[12px] text-ink-mute">{s.academyEmail}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-ink-soft">
                        <span className="tabular block">{s.phone ?? '—'}</span>
                        {s.email && (
                          <span className="block text-[12px] text-ink-mute">{s.email}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {active.length === 0 ? (
                          <span className="text-ink-mute">—</span>
                        ) : (
                          <span className="flex flex-wrap gap-1">
                            {active.map((e) => (
                              <Link
                                key={e.id}
                                to={`/groups/${e.group.id}`}
                                onClick={(ev) => ev.stopPropagation()}
                              >
                                <Badge tone="brand" className="tabular hover:underline">
                                  {e.group.name}
                                </Badge>
                              </Link>
                            ))}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {active.length === 0 ? (
                          <span className="text-ink-mute">—</span>
                        ) : (
                          <span className="flex flex-wrap gap-1">
                            {[...new Map(active.map((e) => [e.project.id, e.project])).values()].map(
                              (project) => (
                                <ProjectBadge key={project.id} project={project} />
                              ),
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <DoneCell assessment={shown?.assessment} />
                      </td>
                      <td className="px-4 py-3">
                        <CertBadge assessment={shown?.assessment} />
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone={s.status === 'ACTIVE' ? 'ok' : 'neutral'}>
                          {STUDENT_STATUS_LABEL[s.status]}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setEditing(s);
                              setModalOpen(true);
                            }}
                            aria-label="რედაქტირება"
                            className="rounded-md p-1.5 text-ink-mute transition hover:bg-canvas hover:text-brand-600"
                          >
                            <Pencil className="size-4" />
                          </button>
                          <button
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setToDelete(s);
                            }}
                            aria-label="წაშლა"
                            className="rounded-md p-1.5 text-ink-mute transition hover:bg-danger-soft hover:text-danger"
                          >
                            <Trash2 className="size-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-line px-4 py-3 text-[13px]">
              <span className="text-ink-mute">
                გვერდი {page} / {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  წინა
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  შემდეგი
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <StudentModal open={modalOpen} onClose={() => setModalOpen(false)} student={editing} />

      <ConfirmDialog
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => void handleDelete()}
        title="სტუდენტის წაშლა"
        message={
          <>
            წაიშლება <strong>{toDelete?.fullName}</strong>. თუ დასწრების ისტორია აქვს, წაშლა
            დაიბლოკება — ამ შემთხვევაში სტატუსი შეცვალე.
          </>
        }
        confirmLabel="წაშლა"
        danger
        busy={remove.isPending}
      />
    </>
  );
}
